import React, { useEffect, useMemo } from 'react';
import styles from './ChatConversation.module.scss';
import { QueryResults } from './QueryResults';
import classNames from 'classnames';
import { LoadingIndicator } from '../internal/loading/LoadingIndicator';
import { Event, ProductGroup } from 'schema-dts';
import { Event as CardEvent } from './QueryResults';
import {
    MinimalNLSearchRequest,
    NLWebAskUserResponse,
    NLWebResponse,
    NLWebResultsResponse,
} from '../../models';
import { isError, isLoading, Status } from '../../domain/status';
import { SaucerImage } from '../internal/images/SaucerImage';
import { HEAP_EVENTS, HEAP_SCREENS, track } from '../../utils/analytics';

export type Message = MinimalNLSearchRequest | NLWebResponse;

const QueryMessage = ({ query }: MinimalNLSearchRequest) => {
    return (
        <div className={classNames(styles.message, styles.queryMessage, styles.userMessage)}>
            {query}
        </div>
    );
};

/**
 * @param displayDate string in the format of "Wed Jul 2 at 6:00pm"
 */
const formatToDisplayDate = (startDate: string | undefined) => {
    if (!startDate) {
        return '';
    }

    const date = new Date(String(startDate));
    return `${date.toLocaleDateString('en-US', { weekday: 'short' })} ${date.toLocaleDateString('en-US', { month: 'short' })} ${date.getDate()} at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
};

const transfromEventResult = (wrapper: any, event: Event): CardEvent => {
    let priceRow = '';
    const offers = 'offers' in event ? event.offers : [];
    if (offers && Array.isArray(offers)) {
        offers.forEach((offer) => {
            if (typeof offer === 'object' && 'lowPrice' in offer) {
                priceRow += `Starts at $${offer.lowPrice}`;
            }
        });
    }

    let location: any = '';
    if ('location' in event) {
        const eventLocation: any = event.location;
        if (eventLocation && 'name' in eventLocation) {
            location = [eventLocation.name, eventLocation.address?.addressLocality]
                .filter((x) => x)
                .join(' - ');
        } else {
            location = 'TBD'; // unknown location string.
        }
    }

    return {
        id: event['@id'] as string,
        summary: wrapper.description,
        url: wrapper.url,
        imageUrl: String(event.image),
        title: wrapper.name,
        dateTimeLocationRow: '',
        dateTime: formatToDisplayDate(event.startDate as string),
        location,
        priceRow: String(priceRow),
    };
};

type EventGroup = Omit<ProductGroup, 'hasVariant'> & { hasVariant: Array<Event> }; // Event Group is a fictional type based on ProductGroup.

const transfromEventGroupResult = (wrapper: any, group: EventGroup): CardEvent => {
    const event = group.hasVariant[0] as Event;

    let priceRow = '';
    const offers = 'offers' in event ? event.offers : [];
    if (offers && Array.isArray(offers)) {
        offers.forEach((offer) => {
            if (typeof offer === 'object' && 'lowPrice' in offer) {
                priceRow += `Starts at $${offer.lowPrice}`;
            }
        });
    }

    let location: any = '';
    if ('location' in group) {
        location = group.location;
        if ('name' in location) {
            location = location.name;
        }
    }

    return {
        id: group['@id'] as string,
        summary: group.description as string,
        url: group.url as string,
        imageUrl: String(group.image),
        title: group.name as string,
        dateTimeLocationRow: '',
        dateTime: formatToDisplayDate(event.startDate as string),
        location,
        priceRow: String(priceRow),
    };
};

const transformResults = (results: Array<any>) => {
    return (results || []).flatMap((result): Array<CardEvent> => {
        if ('schema_object' in result) {
            const schemaObject = result.schema_object;
            if (schemaObject['@type'] === 'Event') {
                return [transfromEventResult(result, schemaObject)];
            }

            if (schemaObject['@type'] === 'EventGroup') {
                return [transfromEventGroupResult(result, schemaObject)];
            }

            return []; // Unknown type
        }

        return []; // Filter out this event, as we were unable to map it.
    });
};

const ResultsMessage = ({
    results,
    summary,
    messagesSent,
}: NLWebResultsResponse & { messagesSent: number }) => {
    const transformedResults = React.useMemo(() => transformResults(results), [results]);
    return (
        <>
            {summary && summary.message ? (
                <div className={classNames(styles.askUserMessage, styles.aiMessage)}>
                    {summary.message}
                </div>
            ) : null}
            <div
                className={classNames(
                    // styles.message,
                    styles.responseMessage,
                    styles.aiMessage,
                )}
            >
                {/* <ResultSummary summary={props.summary} /> */}
                <QueryResults results={transformedResults} messagesSent={messagesSent} />
            </div>
        </>
    );
};

const AskUserMessage = ({ ask_user }: NLWebAskUserResponse) => {
    return (
        <div className={classNames(styles.askUserMessage, styles.aiMessage)}>
            {ask_user.message}
        </div>
    );
};

const LoadingState = () => {
    return (
        <div className={styles.loadingContainer}>
            <LoadingIndicator />
        </div>
    );
};

const ErrorState = (props: { onReload: () => void; messagesSent: number }) => {
    useEffect(() => {
        track({
            eventName: HEAP_EVENTS.ErrorScreenView,
            eventData: {
                Screen: HEAP_SCREENS.CONCIERGE,
                messagesSent: props.messagesSent,
            },
        });
    }, [props.messagesSent]);

    return (
        <div className={styles.errorContainer}>
            <SaucerImage altText="An Error Occurred While Fetching Results" />
            <div className={styles.errorMessage}>Something went wrong on our end</div>
            <button className={styles.reloadButton} onClick={props.onReload}>
                Reload
            </button>
        </div>
    );
};

const ChatMessage = ({ message, messagesSent }: { message: Message; messagesSent: number }) => {
    if ('query' in message) {
        return <QueryMessage {...message} />;
    }
    if ('ask_user' in message) {
        return <AskUserMessage {...message} />;
    }
    if ('results' in message) {
        return <ResultsMessage {...message} messagesSent={messagesSent} />;
    }
    return null;
};

interface ChatConversationProps {
    messages: Message[];
    requestStatus: Status;
    messagesSent: number;
    onReload: () => void;
}

export const ChatConversation = ({
    messages,
    messagesSent,
    requestStatus,
    onReload,
}: ChatConversationProps) => {
    const isLoadingState = useMemo(() => isLoading(requestStatus), [requestStatus]);
    const isErrorState = useMemo(() => isError(requestStatus), [requestStatus]);

    return (
        <div className={styles.root}>
            {messages.map((message, i) => (
                <ChatMessage key={i} message={message} messagesSent={messagesSent} />
            ))}
            {isLoadingState && <LoadingState />}
            {isErrorState && <ErrorState onReload={onReload} messagesSent={messagesSent} />}
        </div>
    );
};
